import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useClaim } from '~/components/ClaimContainer';

import type { ConductorStatusObservation } from '../conductorStatusObservation';

import { useConductorStatus } from './useConductorStatus';

interface ConductorStatusContextValue {
  observation: ConductorStatusObservation | null;
  /** Counts a reader in, so the status is read, until the function it returns counts it out again. */
  addReader: () => () => void;
}

const ConductorStatusContext = createContext<ConductorStatusContextValue | null>(null);

/**
 * Clive's status on the claim, read once for the whole claim page. The claim bar's pill and the Automation First tab
 * both show it, and the status topic's Firestore service holds one listener: a second read would take the listener
 * from the first, and closing either would end it for both. Read while any reader asks for it.
 */
export const ConductorStatusProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { claim } = useClaim({ claimRequired: true });
  const [readerCount, setReaderCount] = useState(0);
  const observation = useConductorStatus(claim.id, { enabled: readerCount > 0 });
  const addReader = useCallback(() => {
    setReaderCount((count) => count + 1);

    return () => setReaderCount((count) => count - 1);
  }, []);
  const value = useMemo(() => ({ observation, addReader }), [observation, addReader]);

  return <ConductorStatusContext.Provider value={value}>{children}</ConductorStatusContext.Provider>;
};

/**
 * Clive's status on the claim while `enabled`, as `useConductorStatus` reads it, and null otherwise. Only under
 * `ConductorStatusProvider`, so a new reader cannot open a second subscription by accident.
 */
export const useConductorStatusObservation = ({ enabled }: { enabled: boolean }): ConductorStatusObservation | null => {
  const context = useContext(ConductorStatusContext);
  if (!context) throw new Error('useConductorStatusObservation must be used within a ConductorStatusProvider');

  const { observation, addReader } = context;
  useEffect(() => (enabled ? addReader() : undefined), [enabled, addReader]);

  return enabled ? observation : null;
};
